import Asset from "./Asset";
import Folder from "./Folder";

class Inspection {
  constructor({
    inspectionId,
    status,
    conditionRating,
    serviceConditionRating,
    createdBy,
    createdAt,
    lastRelatedUpdate,
    id,
    direction,
    dateCaptured,
    chainage,
    inspectionNotes,
    isImperial,
    standard,
    reviewedBy,
    reviewTimestamp,
    workOrder,
    folder,
    asset,
    file,
    footage,
    extraFields,
    toBeMatched,
    startNode,
    endNode,
    location,
    name,
    filename,
    setupLocation,
    chainageUnit,
    assetId,
    material,
    diameter,
    locationStreet,
    locationTown,
    upstreamNode,
    downstreamNode,
    pipeType,
    repairCompletedDate,
    repairPlanId,
    viewDisabled,
  } = {}) {
    this.inspectionId = inspectionId;
    this.status = status;
    this.conditionRating = conditionRating;
    this.serviceConditionRating = serviceConditionRating;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.lastRelatedUpdate = lastRelatedUpdate;
    this.id = id;
    this.direction = direction;
    this.dateCaptured = dateCaptured;
    this.chainage = chainage;
    this.inspectionNotes = inspectionNotes;
    this.isImperial = isImperial;
    this.standard = standard;
    this.reviewedBy = reviewedBy;
    this.reviewTimestamp = reviewTimestamp;
    this.workOrder = workOrder;
    this.folder = folder;
    this.asset = asset;
    this.file = file;
    this.footage = footage;
    this.extraFields = extraFields;
    this.toBeMatched = toBeMatched;
    this.startNode = startNode;
    this.endNode = endNode;
    this.location = location;
    this.name = name;
    this.filename = filename;
    this.setupLocation = setupLocation;
    this.chainageUnit = chainageUnit;
    this.assetId = assetId;
    this.material = material;
    this.diameter = diameter;
    this.locationStreet = locationStreet;
    this.locationTown = locationTown;
    this.upstreamNode = upstreamNode;
    this.downstreamNode = downstreamNode;
    this.pipeType = pipeType;
    this.repairCompletedDate = repairCompletedDate;
    this.repairPlanId = repairPlanId;
    this.viewDisabled = viewDisabled;
  }

  static fromServerModel(data = {}, useHeaderNames = false) {
    if (!useHeaderNames) {
      const folderData = data.folder;
      const assetData = data.asset;
      let asset = null;
      if (assetData && Object.keys(assetData).length > 0) {
        asset = new Asset({
          uuid: assetData.uuid,
          type: assetData.type,
          organisation: assetData.organisation,
          standard: assetData.standard,
          createdAt: assetData.createdAt,
          assetId: assetData.assetId,
          upstreamNode: assetData.upstreamNode,
          downstreamNode: assetData.downstreamNode,
          diameter: assetData.diameter,
          material: assetData.material,
          locationStreet: assetData.locationStreet,
          locationTown: assetData.locationTown,
          pipeType:
            assetData.pipeType === "SS"
              ? "Sewer"
              : assetData.pipeType === "SW"
              ? "Stormwater"
              : assetData.pipeType,
          inspectionsCount: assetData.inspectionsCount,
          repairCompletedDate: assetData.repairCompletedDate,
          extraFields: assetData.extraFields,
        });
      }
      let folder = null;
      if (
        typeof folderData === "number" ||
        typeof folderData === "string"
      ) {
        folder = new Folder({
          id: folderData,
          name: "",
          path: [],
          children: [],
        });
      } else if (folderData && Object.keys(folderData).length > 0) {
        folder = new Folder({
          id: folderData.id,
          name: folderData.jobName || folderData.name || "",
          createdDate: folderData.createdDate,
          standardKey: folderData.standardKey,
          standardName: folderData.standardName || "",
          pipeType: folderData.pipeTypeSewer,
          path: [],
          children: [],
        });
      }
      return new Inspection({
        inspectionId: data.inspectionId,
        status: data.status,
        conditionRating: data.conditionRating,
        serviceConditionRating: data.serviceConditionRating,
        createdBy: data.createdBy,
        createdAt: data.createdAt,
        lastRelatedUpdate: data.lastRelatedUpdate,
        id: data.id,
        direction: data.direction,
        dateCaptured: data.dateCaptured ?? data.Date,
        chainage: data.chainage,
        inspectionNotes: data.inspectionNotes ?? data.GeneralRemarks,
        isImperial: data.isImperial,
        standard: data.standard,
        reviewedBy: data.reviewedBy,
        reviewTimestamp: data.reviewTimestamp,
        workOrder: data.workOrder ?? data.WorkOrder,
        folder: folder,
        asset: asset,
        file: data.file
          ? {
              id: data.file.id,
              url: data.file.url,
              hidden: data.file.hidden,
              filename: data.file.filename,
              fileSize: data.file.fileSize || data.file.file_size,
              fileType: data.file.fileType || data.file.file_type,
              uploadOrg: data.file.uploadOrg || data.file.upload_org,
              targetOrg: data.file.targetOrg || data.file.target_org,
              uploadUser: data.file.uploadUser || data.file.upload_user,
              uploadedBy: data.file.uploadedBy || data.file.uploaded_by,
              createdTime: data.file.createdTime || data.file.created_time,
              jobId: data.file.jobId || data.file.job_id,
              jobTree: data.file.jobTree || data.file.job_tree,
              uploadCompletedTime:
                data.file.uploadCompletedTime ||
                data.file.upload_completed_time,
              uploadCompleted:
                data.file.uploadCompleted || data.file.upload_completed,
              updatedAt: data.file.updatedAt || data.file.updated_at,
              standardKey: data.file.standardKey || data.file.standard_key,
              storageRegion:
                data.file.storageRegion || data.file.storage_region,
              sewerData: data.file.sewerData || data.file.sewer_data,
              downloadUrl: data.file.downloadUrl || data.file.download_url,
              playUrl: data.file.playUrl || data.file.play_url,
              uploadOrgType:
                data.file.uploadOrgType || data.file.upload_org_type,
            }
          : null,
        footage: data.footage
          ? {
              id: typeof data.footage === "string" ? data.footage : data.footage.id,
            }
          : null,
        extraFields: data.extraFields,
        toBeMatched: data.toBeMatched,
        startNode: data.startNode,
        endNode: data.endNode,
        location: data.location,
        name: data.name,
        filename: data.filename,
        setupLocation: data.setupLocation ?? data.SetupLocation,
        chainageUnit: data.chainageUnit,
        assetId: data.assetId,
        material: data.material,
        diameter: data.diameter,
        locationStreet: data.locationStreet,
        locationTown: data.locationTown,
        upstreamNode: data.upstreamNode,
        downstreamNode: data.downstreamNode,
        pipeType: data.pipeType,
        repairCompletedDate: data.repairCompletedDate,
        repairPlanId: data.repairPlan ?? null,
        viewDisabled: data.viewDisabled,
      });
    } else {
      let asset = null;
      if (data.asset && Object.keys(data.asset).length > 0) {
        asset = new Asset({
          uuid: data.asset.uuid,
          type: data.asset.type,
          organisation: data.asset.organisation,
          standard: data.asset.standard,
          createdAt: data.asset.createdAt,
          assetId: data.asset.AssetID,
          upstreamNode: data.asset.UpstreamNode,
          downstreamNode: data.asset.DownstreamNode,
          diameter: data.asset.HeightDiameter,
          material: data.asset.Material,
          locationStreet: data.asset.LocationStreet,
          locationTown: data.asset.LocationTown,
          pipeType:
            data.asset.UseOfDrainSewer === "SS"
              ? "Sewer"
              : data.asset.UseOfDrainSewer === "SW"
              ? "Stormwater"
              : data.asset.UseOfDrainSewer,
          inspectionsCount: data.asset.inspectionsCount,
          extraFields: data.asset.extraFields,
        });
      }
      let folder = null;
      if (data.folder && Object.keys(data.folder).length > 0) {
        folder = new Folder({
          id: data.folder.id,
          name: data.folder.job_name || data.folder.name || "",
          createdDate: data.folder.created_date,
          standardKey: data.folder.standard_key,
          pipeType: data.folder.pipe_type_sewer,
          path: [],
          children: [],
        });
      }

      return new Inspection({
        inspectionId: data.uuid,
        status: data.status,
        conditionRating: data.structuralGrade,
        serviceConditionRating: data.serviceGrade,
        createdBy: data.createdBy,
        createdAt: data.createdAt,
        lastRelatedUpdate: data.lastRelatedUpdate,
        id: data.legacyId,
        direction: data.Direction,
        dateCaptured: data.Date,
        chainage: data.LengthSurveyed,
        inspectionNotes: data.GeneralRemarks,
        isImperial: data.IsImperial,
        standard: data.Standard,
        reviewedBy: data.ReviewedBy,
        reviewTimestamp: data.Time,
        workOrder: data.WorkOrder,
        folder: folder,
        asset: asset,
        file: {
          id: data.file.id,
          url: data.file.url,
          hidden: data.file.hidden,
          filename: data.file.filename,
          fileSize: data.file.file_size,
          fileType: data.file.file_type,
          uploadOrg: data.file.upload_org,
          targetOrg: data.file.target_org,
          uploadUser: data.file.upload_user,
          uploadedBy: data.file.uploaded_by,
          createdTime: data.file.created_time,
          jobId: data.file.job_id,
          jobTree: data.file.job_tree,
          uploadCompletedTime: data.file.upload_completed_time,
          uploadCompleted: data.file.upload_completed,
          updatedAt: data.file.updated_at,
          standardKey: data.file.standard_key,
          storageRegion: data.file.storage_region,
          sewerData: data.file.sewer_data,
          downloadUrl: data.file.download_url,
          playUrl: data.file.play_url,
          uploadOrgType: data.file.upload_org_type,
        },
        extraFields: data.extraFields,
        toBeMatched: data.toBeMatched,
        startNode: data.start_node,
        endNode: data.end_node,
        location: data.location,
        name: data.name,
        filename: data.filename,
        setupLocation: data.SetupLocation,
        chainageUnit: data.chainage_unit,
        assetId: data.AssetID,
        material: data.Material,
        diameter: data.HeightDiameter,
        locationStreet: data.LocationStreet,
        locationTown: data.LocationTown,
        upstreamNode: data.UpstreamNode,
        downstreamNode: data.DownstreamNode,
        pipeType:
          data.UseOfDrainSewer === "SS"
            ? "Sewer"
            : data.UseOfDrainSewer === "SW"
            ? "Stormwater"
            : data.UseOfDrainSewer,
        repairCompletedDate: data.repairCompletedDate || null,
        repairPlanId: data.repairPlan ?? null,
        viewDisabled: data.viewDisabled,
      });
    }
  }

  toServerModel() {
    return {
      inspectionId: this.inspectionId,
      status: this.status,
      conditionRating: this.conditionRating,
      serviceConditionRating: this.serviceConditionRating,
      createdBy: this.createdBy,
      createdAt: this.createdAt,
      lastRelatedUpdate: this.lastRelatedUpdate,
      id: this.id,
      direction: this.direction,
      dateCaptured: this.dateCaptured,
      chainage: this.chainage,
      inspectionNotes: this.inspectionNotes,
      isImperial: this.isImperial,
      standard: this.standard,
      reviewedBy: this.reviewedBy,
      reviewTimestamp: this.reviewTimestamp,
      workOrder: this.workOrder,
      folder: this.folder ? this.folder.toServerModel() : null,
      asset: this.asset ? this.asset.toServerModel() : null,
      file: this.file ? this.file.toServerModel() : null,
      extraFields: this.extraFields,
      toBeMatched: this.toBeMatched,
      startNode: this.startNode,
      endNode: this.endNode,
      location: this.location,
      name: this.name,
      filename: this.filename,
      setupLocation: this.setupLocation,
      chainageUnit: this.chainageUnit,
      assetId: this.assetId,
      material: this.material,
      diameter: this.diameter,
      locationStreet: this.locationStreet,
      locationTown: this.locationTown,
      upstreamNode: this.upstreamNode,
      downstreamNode: this.downstreamNode,
      pipeType: this.pipeType,
      repairCompletedDate: this.repairCompletedDate,
      viewDisabled: this.viewDisabled,
    };
  }

  static fromLinkedInspectionServerModel(data = {}) {
    const folderData = data.folder;
    let folder = null;
    if (folderData && Object.keys(folderData).length > 0) {
      folder = new Folder({
        id: folderData.id,
        name: folderData.jobName || folderData.name || "",
        created_date: folderData.createdDate,
        standard_key: folderData.standardKey,
        pipeType: folderData.pipeTypeSewer,
        path: [],
        children: [],
      });
    }
    return new Inspection({
      inspectionId: data.uuid,
      status: data.status,
      conditionRating: data.conditionRating,
      serviceConditionRating: data.serviceConditionRating,
      createdBy: data.createdBy,
      createdAt: data.createdAt,
      lastRelatedUpdate: data.lastRelatedUpdate,
      id: data.legacyId,
      direction: data.direction,
      dateCaptured: data.Date,
      chainage: data.LengthSurveyed,
      inspectionNotes: data.inspectionNotes,
      isImperial: data.isImperial,
      standard: data.standard,
      reviewedBy: data.reviewedBy,
      reviewTimestamp: data.reviewTimestamp,
      workOrder: data.workOrder,
      folder: folder,
      asset: {
        uuid: data.asset.uuid,
        type: data.asset.type,
        organisation: data.asset.organisation,
        standard: data.asset.standard,
        createdAt: data.asset.createdAt,
        assetId: data.asset.AssetID,
        upstreamNode: data.asset.UpstreamNode,
        downstreamNode: data.asset.DownstreamNode,
        diameter: data.asset.HeightDiameter,
        material: data.asset.Material,
        locationStreet: data.asset.LocationStreet,
        locationTown: data.asset.LocationTown,
        pipeType:
          data.asset.UseOfDrainSewer === "SS"
            ? "Sewer"
            : data.asset.UseOfDrainSewer === "SW"
            ? "Stormwater"
            : data.asset.UseOfDrainSewer,
        inspectionsCount: data.asset.inspectionsCount,
        extraFields: data.asset.extraFields,
      },
      file: {
        id: data.file.id,
        url: data.file.url,
        hidden: data.file.hidden,
        filename: data.file.filename,
        fileSize: data.file.file_size,
        fileType: data.file.file_type,
        uploadOrg: data.file.upload_org,
        targetOrg: data.file.target_org,
        uploadUser: data.file.upload_user,
        uploadedBy: data.file.uploaded_by,
        createdTime: data.file.createdTime,
        jobId: data.file.job_id,
        jobTree: data.file.job_tree,
        uploadCompletedTime: data.file.upload_completed_time,
        uploadCompleted: data.file.upload_completed,
        updatedAt: data.file.updatedAt,
        standardKey: data.file.standard_key,
        storageRegion: data.file.storage_region,
        sewerData: data.file.sewer_data,
        downloadUrl: data.file.download_url,
        playUrl: data.file.play_url,
        uploadOrgType: data.file.upload_org_type,
      },
      extraFields: data.extraFields,
      toBeMatched: data.toBeMatched,
      startNode: data.startNode,
      endNode: data.endNode,
      location: data.location,
      name: data.name,
      filename: data.filename,
      setupLocation: data.setupLocation,
      chainageUnit: data.chainageUnit,
      assetId: data.assetId,
      material: data.material,
      diameter: data.diameter,
      locationStreet: data.locationStreet,
      locationTown: data.locationTown,
      upstreamNode: data.upstreamNode,
      downstreamNode: data.downstreamNode,
      pipeType: data.pipeType,
      repairCompletedDate: data.repairCompletedDate,
      repairPlanId: data.repairPlan ?? null,
      viewDisabled: data.viewDisabled,
    });
  }
}

export default Inspection;
